import { tradingClient } from "@/shared/rpc/clients";
import { saveUserSetting } from "@/shared/stores/saveUserSetting";
import { userSettingsStore } from "@/shared/stores/UserSettingsStore";
import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { makeAutoObservable, runInAction } from "mobx";
import { makePersistable } from "mobx-persist-store";

const POLLING_INTERVAL_MS = 30_000;

// Two independent, server-persisted lists (via the generic user-settings RPC so
// both follow the account across devices; localStorage stays as an instant cache):
//   - SELECTED: the pairs pinned as tabs in the selector + the active pair. Set
//     by clicking a pair row. Stored under the legacy "favorite-pairs" key for
//     backward compatibility with accounts that predate the favorites split.
//   - FAVORITES: pairs the user stars. Set by clicking the star. Shown in the
//     "Favorite" category. Starring never changes the active/selected tabs.
const SELECTED_SECTION = "trading";
const SELECTED_KEY = "favorite-pairs";
const FAVORITES_SECTION = "trading";
const FAVORITES_KEY = "favorites";
const SELECTED_SAVE_DEBOUNCE_MS = 400;

function pairSnapshotKey(pair: PairMessage | null | undefined): string {
  if (!pair) return "";
  return [
    pair.id,
    pair.symbol,
    pair.type,
    pair.isTradingEnabled,
    pair.isStreaming,
    pair.isOtc,
    pair.isAdminEnabled,
    pair.pricePrecision,
    pair.tps,
    pair.minTtl ?? "",
    pair.maxTtl ?? "",
    pair.betSettings
      .map((period) => `${period.id}:${period.symbol}:${period.ttl}:${period.timeframe ?? ""}:${period.winPercentage}:${period.minRaisePoints}`)
      .join(","),
  ].join("|");
}

class PairsStore {
  pairs: PairMessage[] = [];
  selectedPairs: PairMessage[] = [];
  favoritePairs: PairMessage[] = [];
  activePair: PairMessage | null = null;
  initialized = false;
  hydrated = false;
  private fetching = false;
  private pollingIntervalId: ReturnType<typeof setInterval> | null = null;
  // Ids of the persisted settings entries (null until first save/restore).
  private selectedSettingId: string | null = null;
  private favoritesSettingId: string | null = null;
  // Suppresses server saves while we are restoring state from server/localStorage.
  private restoring = false;
  // Serializes saves so a null id can't trigger two concurrent creates.
  private savingSelected = false;
  private pendingSelectedSave = false;
  private selectedSaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private savingFavorites = false;
  private pendingFavoritesSave = false;

  constructor() {
    makeAutoObservable(this, {
      fetching: false,
      pollingIntervalId: false,
      selectedSettingId: false,
      favoritesSettingId: false,
      restoring: false,
      savingSelected: false,
      pendingSelectedSave: false,
      selectedSaveTimeoutId: false,
      savingFavorites: false,
      pendingFavoritesSave: false,
    } as any);

    makePersistable(this, {
      name: "PairsStore",
      properties: ["selectedPairs", "favoritePairs", "activePair"],
      storage: localStorage,
    }).then(() => {
      runInAction(() => {
        this.hydrated = true;

        if (this.pairs.length > 0 && !this.initialized) {
          this.initialized = true;
          void this.restoreState();
        }
      });
    });
  }

  /**
   * True when the user has selected a pair whose trading is currently
   * disabled (off-schedule or admin-paused). Drives the greyed-out
   * state and the "trading paused" banner in the trading view.
   */
  get isActivePairUnavailable(): boolean {
    return this.activePair !== null && this.activePair.isTradingEnabled === false;
  }

  async fetchPairs(silent = false) {
    if (!silent && this.fetching) return;
    if (!silent) this.fetching = true;

    try {
      const response = await tradingClient.listTradingPairs({});

      const rawPairs = response.data ?? [];
      if (!Array.isArray(rawPairs)) {
        console.warn("Pairs response is not an array:", rawPairs);
        return;
      }

      runInAction(() => {
        this.pairs = rawPairs;

        // On silent poll: refresh activePair and prune inactive selectedPairs
        if (silent) {
          if (this.activePair) {
            const updated = rawPairs.find((p) => p.id === this.activePair!.id);
            if (updated && pairSnapshotKey(updated) !== pairSnapshotKey(this.activePair)) {
              this.activePair = updated;
            }
          }
          this.selectedPairs = this.selectedPairs
            .map((sp) => rawPairs.find((p) => p.id === sp.id) ?? sp)
            .filter((p) => p.id === this.activePair?.id || (p.isTradingEnabled && this.hasTradableBetPeriods(p)));
          if (this.selectedPairs.length === 0) {
            const defaultPair = this.getDefaultPair();
            if (defaultPair) this.selectedPairs = [defaultPair];
          }
          // Keep favorite snapshots fresh; drop any that no longer exist.
          this.favoritePairs = this.favoritePairs
            .map((fp) => rawPairs.find((p) => p.id === fp.id))
            .filter((p): p is PairMessage => p !== undefined);
        }

        if (!silent && this.hydrated) {
          this.initialized = true;
          void this.restoreState();
        }
      });

      if (!silent) {
        this.startPolling();
      }
    } catch (e) {
      console.error("Failed to load pairs:", e);
    } finally {
      if (!silent) this.fetching = false;
    }
  }

  private startPolling() {
    this.stopPolling();
    this.pollingIntervalId = setInterval(() => {
      this.fetchPairs(true);
    }, POLLING_INTERVAL_MS);
  }

  private stopPolling() {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  /**
   * Restore both lists from the server settings first (source of truth across
   * devices); fall back to the localStorage-persisted state when the account
   * has no saved data yet or the request fails.
   */
  private async restoreState() {
    this.restoring = true;
    try {
      await userSettingsStore.load();

      // --- Favorites (starred pairs) ---
      const favEntry = userSettingsStore.getSetting(FAVORITES_SECTION, FAVORITES_KEY);
      if (favEntry) {
        runInAction(() => {
          this.favoritesSettingId = favEntry.id;
        });
      }
      const favValue = favEntry?.value as { pairIds?: unknown } | undefined;
      const favIds = Array.isArray(favValue?.pairIds)
        ? (favValue!.pairIds as string[])
        : this.favoritePairs.map((p) => p.id);
      runInAction(() => {
        this.favoritePairs = favIds
          .map((id) => this.pairs.find((p) => p.id === id))
          .filter((p): p is PairMessage => p !== undefined);
      });

      // --- Selected pairs (tabs + active) ---
      const selEntry = userSettingsStore.getSetting(SELECTED_SECTION, SELECTED_KEY);
      const selValue = selEntry?.value as { pairIds?: unknown; activePairId?: unknown } | undefined;

      if (selEntry) {
        runInAction(() => {
          this.selectedSettingId = selEntry.id;
        });
      }

      if (selValue && Array.isArray(selValue.pairIds)) {
        const restored = (selValue.pairIds as string[])
          .map((id) => this.pairs.find((p) => p.id === id))
          .filter((p): p is PairMessage => p !== undefined && this.hasTradableBetPeriods(p));

        if (restored.length > 0) {
          runInAction(() => {
            this.selectedPairs = restored;
            const active =
              typeof selValue.activePairId === "string"
                ? restored.find((p) => p.id === selValue.activePairId)
                : undefined;
            this.setActivePair(active ?? restored[0]!);
          });
          return;
        }
      }

      // No usable server selection → fall back to localStorage / defaults.
      runInAction(() => this.restoreFromPersistedState());
    } catch {
      runInAction(() => this.restoreFromPersistedState());
    } finally {
      runInAction(() => {
        this.restoring = false;
      });
    }
  }

  /**
   * Persist the current selection (pinned tab pairs + active pair) to the
   * server. Best-effort and serialized so a not-yet-created entry can't race
   * into two concurrent creates. No-op while restoring or before initial load.
   */
  private async persistSelectedPairs() {
    if (this.restoring || !this.initialized) return;
    if (this.selectedSaveTimeoutId) {
      clearTimeout(this.selectedSaveTimeoutId);
      this.selectedSaveTimeoutId = null;
    }
    if (this.savingSelected) {
      this.pendingSelectedSave = true;
      return;
    }
    this.savingSelected = true;

    const value = {
      pairIds: this.selectedPairs.map((p) => p.id),
      activePairId: this.activePair?.id ?? null,
    };
    const ref = { current: this.selectedSettingId };

    try {
      await saveUserSetting(SELECTED_SECTION, SELECTED_KEY, value, ref);
    } finally {
      runInAction(() => {
        this.selectedSettingId = ref.current;
        this.savingSelected = false;
      });
      if (this.pendingSelectedSave) {
        this.pendingSelectedSave = false;
        void this.persistSelectedPairs();
      }
    }
  }

  private schedulePersistSelectedPairs() {
    if (this.restoring || !this.initialized) return;
    if (this.selectedSaveTimeoutId) clearTimeout(this.selectedSaveTimeoutId);
    this.selectedSaveTimeoutId = setTimeout(() => {
      this.selectedSaveTimeoutId = null;
      void this.persistSelectedPairs();
    }, SELECTED_SAVE_DEBOUNCE_MS);
  }

  /**
   * Persist the current favorites (starred pairs) to the server. Serialized
   * like persistSelectedPairs. No-op while restoring or before initial load.
   */
  private async persistFavorites() {
    if (this.restoring || !this.initialized) return;
    if (this.savingFavorites) {
      this.pendingFavoritesSave = true;
      return;
    }
    this.savingFavorites = true;

    const value = { pairIds: this.favoritePairs.map((p) => p.id) };
    const ref = { current: this.favoritesSettingId };

    try {
      await saveUserSetting(FAVORITES_SECTION, FAVORITES_KEY, value, ref);
    } finally {
      runInAction(() => {
        this.favoritesSettingId = ref.current;
        this.savingFavorites = false;
      });
      if (this.pendingFavoritesSave) {
        this.pendingFavoritesSave = false;
        void this.persistFavorites();
      }
    }
  }

  private restoreFromPersistedState() {
    if (this.selectedPairs.length > 0) {
      const validPairs = this.selectedPairs
        .map((sp) => this.pairs.find((p) => p.id === sp.id))
        .filter((p): p is PairMessage => p !== undefined);

      const tradablePairs = validPairs.filter((pair) => this.hasTradableBetPeriods(pair));
      if (tradablePairs.length > 0) {
        this.selectedPairs = tradablePairs;

        if (this.activePair) {
          const actual = this.pairs.find((p) => p.id === this.activePair?.id);
          this.setActivePair(actual && this.hasTradableBetPeriods(actual) ? actual : tradablePairs[0]!);
        } else {
          this.setActivePair(tradablePairs[0]!);
        }
        return;
      }
    }

    const defaultPair = this.getDefaultPair();
    if (defaultPair) {
      this.selectedPairs = [defaultPair];
      this.setActivePair(defaultPair);
    }
  }

  setSelectedPairs(newPairs: PairMessage[]) {
    this.selectedPairs = newPairs;
    this.setActivePair(newPairs.at(-1) || null);
    this.schedulePersistSelectedPairs();
  }

  setActivePair(pair: PairMessage | null) {
    this.activePair = pair;
  }

  addPair(pair: PairMessage) {
    if (this.selectedPairs.find((p) => p.id === pair.id)) return;

    this.selectedPairs.push(pair);
    this.schedulePersistSelectedPairs();
  }

  removePair(pairId: string) {
    this.selectedPairs = this.selectedPairs.filter((p) => p.id !== pairId);

    if (this.activePair?.id === pairId) {
      if (this.selectedPairs && this.selectedPairs.length > 0) {
        this.setActivePair(this.selectedPairs[0]!);
      } else {
        const defaultPair = this.getDefaultPair();
        if (defaultPair) {
          this.selectedPairs = [defaultPair];
          this.setActivePair(defaultPair);
        } else {
          this.setActivePair(null);
        }
      }
    }
    this.schedulePersistSelectedPairs();
  }

  selectPairAsActive(pairId: string) {
    const pair = this.selectedPairs.find((p) => p.id === pairId);
    if (pair) {
      this.setActivePair(pair);
      this.pruneInactivePairs();
      this.schedulePersistSelectedPairs();
    }
  }

  /** Whether a pair is starred as a favorite. */
  isFavorite(pairId: string): boolean {
    return this.favoritePairs.some((p) => p.id === pairId);
  }

  /**
   * Toggle a pair's favorite (starred) state. Independent of the selected tab
   * list and the active pair — starring only adds/removes from favorites.
   */
  toggleFavorite(pair: PairMessage) {
    const exists = this.favoritePairs.some((p) => p.id === pair.id);
    this.favoritePairs = exists
      ? this.favoritePairs.filter((p) => p.id !== pair.id)
      : [...this.favoritePairs, pair];
    void this.persistFavorites();
  }

  private pruneInactivePairs() {
    this.selectedPairs = this.selectedPairs.filter((sp) => {
      if (sp.id === this.activePair?.id) return true;
      const fresh = this.pairs.find((p) => p.id === sp.id);
      return fresh ? fresh.isTradingEnabled && this.hasTradableBetPeriods(fresh) : false;
    });
  }

  replaceActivePair(newPair: PairMessage) {
    if (!this.activePair) {
      this.addPair(newPair);
      this.setActivePair(newPair);
      return;
    }

    const index = this.selectedPairs.findIndex((p) => p.id === this.activePair!.id);
    if (index !== -1) {
      this.selectedPairs[index] = newPair;
    } else {
      this.selectedPairs.push(newPair);
    }

    this.setActivePair(newPair);
    this.schedulePersistSelectedPairs();
  }

  getDefaultPair(): PairMessage | undefined {
    return (
      this.pairs.find((pair) => pair.symbol === "BTC/USD" && this.hasTradableBetPeriods(pair)) ||
      this.pairs.find((pair) => this.hasTradableBetPeriods(pair)) ||
      this.pairs.find((pair) => pair.symbol === "BTC/USD") ||
      this.pairs[0]
    );
  }

  private hasTradableBetPeriods(pair?: PairMessage | null): boolean {
    if (pair?.betSettings?.some((setting) => setting.ttl > 0)) return true;
    // Pair without presets is still tradable if it has a valid duration range for manual entry
    return (pair?.minDurationSeconds ?? 0) > 0 && (pair?.maxDurationSeconds ?? 0) > 0;
  }

  reset() {
    this.stopPolling();
    this.pairs = [];
    this.selectedPairs = [];
    this.favoritePairs = [];
    this.activePair = null;
    this.initialized = false;
    this.selectedSettingId = null;
    this.favoritesSettingId = null;
    this.restoring = false;
    this.savingSelected = false;
    this.pendingSelectedSave = false;
    if (this.selectedSaveTimeoutId) {
      clearTimeout(this.selectedSaveTimeoutId);
      this.selectedSaveTimeoutId = null;
    }
    this.savingFavorites = false;
    this.pendingFavoritesSave = false;
  }
}

export const pairsStore = new PairsStore();
